import React, { useEffect, useState } from "react";
import { Helmet } from "react-helmet";
import { Link } from "react-router-dom";
import {
  Search, Bot, Mic, Code, MapPin, ArrowRight, BookOpen,
  Globe, Sparkles, BarChart2, TrendingUp, Target, Shield,
  Zap, Eye, MessageSquare, FileText, Users, Trophy, ArrowUpRight
} from "lucide-react";
import { Button } from "@/components/ui/button";

const API_URL = process.env.REACT_APP_BACKEND_URL;

const PILLARS = [
  {
    id: "seo",
    label: "SEO",
    icon: Search,
    color: "from-green-500/20 to-green-500/5",
    border: "border-green-500/30",
    text: "text-green-400",
    desc: "The foundation of online visibility",
    links: [
      { to: "/services/seo", label: "SEO Services" },
      { to: "/services/local-seo", label: "Local SEO" },
      { to: "/services/gbp-optimization", label: "GBP Optimization" },
      { to: "/blog/seo-complete-guide", label: "Complete SEO Guide" },
    ],
  },
  {
    id: "aeo",
    label: "AEO",
    icon: Mic,
    color: "from-emerald-500/20 to-emerald-500/5",
    border: "border-emerald-500/30",
    text: "text-emerald-400",
    desc: "Featured snippets, voice search, PAA",
    links: [
      { to: "/what-is-aeo", label: "What Is AEO?" },
      { to: "/services/aeo", label: "AEO Services" },
      { to: "/aeo-vs-geo-vs-seo", label: "AEO vs GEO vs SEO" },
    ],
  },
  {
    id: "geo",
    label: "GEO",
    icon: Bot,
    color: "from-purple-500/20 to-purple-500/5",
    border: "border-purple-500/30",
    text: "text-purple-400",
    desc: "AI citations in ChatGPT, Perplexity, AI Overviews",
    links: [
      { to: "/what-is-geo", label: "What Is GEO?" },
      { to: "/services/geo", label: "GEO Services" },
      { to: "/aeo-geo", label: "GEO/AEO Pillar Page" },
      { to: "/blog/ai-search-statistics-2026", label: "AI Search Statistics 2026" },
    ],
  },
  {
    id: "llmo",
    label: "LLMO",
    icon: Code,
    color: "from-blue-500/20 to-blue-500/5",
    border: "border-blue-500/30",
    text: "text-blue-400",
    desc: "Training data, RAG, entity authority",
    links: [
      { to: "/what-is-llmo", label: "What Is LLMO?" },
      { to: "/services/llm-optimization", label: "LLM Optimization Services" },
      { to: "/blog/how-llms-choose-businesses", label: "How LLMs Choose Businesses" },
    ],
  },
];

const GUIDES = [
  { to: "/blog/ai-for-seo-guide", label: "AI for SEO Guide", icon: Bot },
  { to: "/blog/google-business-profile-guide", label: "Google Business Profile Guide", icon: Globe },
  { to: "/blog/best-ai-tools", label: "Best AI Tools 2026", icon: Sparkles },
  { to: "/blog/ai-agents-for-business", label: "AI Agents for Business", icon: Zap },
  { to: "/guides/local-seo-ranking-factors", label: "Local SEO Ranking Factors", icon: TrendingUp },
  { to: "/blog/seo-complete-guide", label: "Complete SEO Guide", icon: Search },
];

const CITATION_ARTICLES = [
  { to: "/blog/ai-search-statistics-2026", label: "AI Search Statistics 2026", desc: "How ChatGPT, Perplexity & AI Overviews are changing search" },
  { to: "/blog/how-llms-choose-businesses", label: "How LLMs Choose Which Businesses to Recommend", desc: "The AI recommendation pipeline explained" },
  { to: "/blog/geo-vs-seo-roi-comparison", label: "GEO vs SEO: ROI Comparison (2026)", desc: "Which drives more revenue for Texas businesses" },
  { to: "/blog/local-seo-vs-geo", label: "Local SEO vs GEO: Which Matters More?", desc: "For small businesses navigating the new search landscape" },
  { to: "/blog/complete-guide-ai-search-optimization", label: "Complete Guide to AI Search Optimization", desc: "AEO + GEO + LLMO combined strategy" },
  { to: "/blog/social-media-hooks-that-stop-the-scroll", label: "Social Media Hooks That Stop the Scroll", desc: "The psychology + 10 fill-in-the-blank formulas (free 100-hook swipe file)" },
];

const CITIES = [
  { name: "El Paso", slug: "el-paso" },
  { name: "Lubbock", slug: "lubbock" },
  { name: "Waco", slug: "waco" },
  { name: "Killeen", slug: "killeen" },
  { name: "Amarillo", slug: "amarillo" },
];

const SOURCE_COLORS = {
  ChatGPT: "bg-green-500/20 text-green-400 border-green-500/30",
  Perplexity: "bg-blue-500/20 text-blue-400 border-blue-500/30",
  "AI Overviews": "bg-yellow-500/20 text-yellow-400 border-yellow-500/30",
  Claude: "bg-orange-500/20 text-orange-400 border-orange-500/30",
  Copilot: "bg-cyan-500/20 text-cyan-400 border-cyan-500/30",
};

const RANK_STYLES = [
  "text-yellow-400", // #1
  "text-gray-300",   // #2
  "text-amber-600",  // #3
  "text-gray-500",   // #4
  "text-gray-600",   // #5
];

const SLUG_TITLES = {
  "ai-search-statistics-2026": "AI Search Statistics 2026",
  "how-llms-choose-businesses": "How LLMs Choose Businesses",
  "geo-vs-seo-roi-comparison": "GEO vs SEO: ROI Comparison",
  "local-seo-vs-geo": "Local SEO vs GEO",
  "complete-guide-ai-search-optimization": "Complete AI Search Optimization Guide",
};

function CitationLeaderboard() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetch(`${API_URL}/api/citations/leaderboard`)
      .then((r) => r.json())
      .then((d) => { setData(d); setLoading(false); })
      .catch(() => setLoading(false));
  }, []);

  if (loading) {
    return (
      <div className="bg-white/5 border border-white/10 rounded-2xl p-8 animate-pulse" data-testid="leaderboard-loading">
        <div className="h-6 bg-white/10 rounded w-48 mb-6" />
        {[...Array(3)].map((_, i) => (
          <div key={i} className="h-16 bg-white/5 rounded-xl mb-3" />
        ))}
      </div>
    );
  }

  if (!data || !data.articles || data.articles.length === 0) return null;

  return (
    <div className="bg-gradient-to-br from-white/[0.06] to-white/[0.02] border border-white/10 rounded-2xl p-6 md:p-8" data-testid="citation-leaderboard">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-yellow-500/10 border border-yellow-500/20 flex items-center justify-center">
            <Trophy className="w-5 h-5 text-yellow-400" />
          </div>
          <div>
            <h3 className="text-lg font-bold text-white">Most Cited This Month</h3>
            <p className="text-xs text-gray-500">AI citation leaderboard — {data.month}</p>
          </div>
        </div>
        <span className="text-[10px] text-gray-600 hidden sm:block">
          Updated {new Date(data.updated).toLocaleDateString("en-US", { month: "short", day: "numeric" })}
        </span>
      </div>

      <div className="space-y-3">
        {data.articles.map((article, i) => {
          const title = SLUG_TITLES[article.slug] || article.slug.replace(/-/g, " ");
          return (
            <Link
              key={article.slug}
              to={`/blog/${article.slug}`}
              className="flex items-center gap-4 bg-black/20 border border-white/5 rounded-xl px-4 py-3 hover:border-[#2ABFBF]/30 hover:bg-white/[0.04] transition-all group"
              data-testid={`leaderboard-item-${i}`}
            >
              {/* Rank */}
              <span className={`text-2xl font-black w-8 text-center shrink-0 ${RANK_STYLES[i] || "text-gray-600"}`}>
                {i + 1}
              </span>

              {/* Article info */}
              <div className="flex-1 min-w-0">
                <p className="text-sm font-semibold text-white truncate group-hover:text-[#2ABFBF] transition-colors">
                  {title}
                </p>
                <div className="flex items-center gap-2 mt-1 flex-wrap">
                  {Object.entries(article.sources).map(([src, count]) => (
                    <span
                      key={src}
                      className={`text-[10px] px-2 py-0.5 rounded-full border ${SOURCE_COLORS[src] || "bg-gray-500/20 text-gray-400 border-gray-500/30"}`}
                    >
                      {src} {count}
                    </span>
                  ))}
                </div>
              </div>

              {/* Citation count + trend */}
              <div className="text-right shrink-0">
                <span className="text-lg font-bold text-white">{article.total}</span>
                <span className="text-xs text-gray-500 ml-1">citations</span>
                {article.trend !== undefined && (
                  <div className={`text-xs mt-0.5 ${article.trend >= 0 ? "text-green-400" : "text-red-400"}`}>
                    {article.trend >= 0 ? "↑" : "↓"} {Math.abs(article.trend)}%
                  </div>
                )}
              </div>

              <ArrowUpRight className="w-4 h-4 text-gray-600 group-hover:text-[#2ABFBF] transition-colors shrink-0" />
            </Link>
          );
        })}
      </div>
    </div>
  );
}

const LearnPage = () => {
  // Track AI referral on mount
  useEffect(() => {
    import("@/lib/constants").then(({ setContentSource }) => setContentSource("learn-hub"));
    const ref = document.referrer || "";
    const path = window.location.pathname;
    const slug = path.startsWith("/blog/") ? path.replace("/blog/", "").replace(/\/$/, "") : null;
    if (slug && ref) {
      fetch(`${API_URL}/api/citations/track`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ slug, referrer: ref }),
      }).catch(() => {});
    }
  }, []);

  return (
  <div className="min-h-screen pt-28 pb-20" data-testid="learn-page">
    <Helmet>
      <title>Learn: AI Search Optimization Knowledge Hub | Digital Media Ninja</title>
      <meta name="description" content="Your complete guide to SEO, AEO, GEO, and LLM optimization. Interactive knowledge hub mapping every resource, guide, and service page." />
      <link rel="canonical" href="https://digitalmedianinja.com/learn/" />
    </Helmet>

    {/* Hero */}
    <section className="container-custom max-w-6xl mx-auto px-4 mb-16">
      <div className="text-center">
        <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#2ABFBF]/10 border border-[#2ABFBF]/20 text-[#2ABFBF] text-sm font-medium mb-6">
          <BookOpen className="w-4 h-4" /> Knowledge Hub
        </div>
        <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white mb-6 leading-tight">
          The AI Search<br />Optimization Map
        </h1>
        <p className="text-lg text-gray-400 max-w-3xl mx-auto">
          Everything you need to understand modern search visibility — from traditional SEO to AI citations. Explore definitions, service pages, guides, and data-driven articles.
        </p>
      </div>
    </section>

    {/* The Stack Visual */}
    <section className="container-custom max-w-5xl mx-auto px-4 mb-20">
      <div className="relative">
        {/* Connection lines (CSS-only) */}
        <div className="hidden lg:block absolute left-1/2 top-0 bottom-0 w-px bg-gradient-to-b from-green-500/30 via-purple-500/30 to-blue-500/30" />

        <div className="space-y-6">
          {PILLARS.map((pillar, i) => {
            const Icon = pillar.icon;
            return (
              <div key={pillar.id} className="relative" data-testid={`pillar-${pillar.id}`}>
                <div className={`bg-gradient-to-r ${pillar.color} border ${pillar.border} rounded-2xl p-6 md:p-8`}>
                  <div className="flex flex-col md:flex-row md:items-start gap-6">
                    {/* Left: Pillar info */}
                    <div className="md:w-1/3">
                      <div className="flex items-center gap-3 mb-3">
                        <div className={`w-12 h-12 rounded-xl bg-black/30 flex items-center justify-center`}>
                          <Icon className={`w-6 h-6 ${pillar.text}`} />
                        </div>
                        <div>
                          <h2 className="text-2xl font-bold text-white">{pillar.label}</h2>
                          <span className="text-xs text-gray-400">Layer {i + 1}</span>
                        </div>
                      </div>
                      <p className="text-gray-400 text-sm">{pillar.desc}</p>
                    </div>
                    {/* Right: Links */}
                    <div className="md:w-2/3 grid sm:grid-cols-2 gap-2">
                      {pillar.links.map((link, j) => (
                        <Link
                          key={j}
                          to={link.to}
                          className="flex items-center gap-2 bg-black/20 border border-white/10 rounded-lg px-4 py-3 text-sm text-gray-300 hover:text-white hover:border-white/20 transition-all group"
                        >
                          <FileText className="w-4 h-4 text-gray-500 group-hover:text-[#2ABFBF] transition-colors shrink-0" />
                          <span className="truncate">{link.label}</span>
                          <ArrowRight className="w-3 h-3 ml-auto opacity-0 group-hover:opacity-100 transition-opacity shrink-0" />
                        </Link>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </section>

    {/* Citation-Bait Articles */}
    <section className="container-custom max-w-5xl mx-auto px-4 mb-20">
      <div className="flex items-center gap-3 mb-8">
        <BarChart2 className="w-6 h-6 text-[#2ABFBF]" />
        <h2 className="text-2xl font-bold text-white">Data & Research</h2>
      </div>
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {CITATION_ARTICLES.map((article, i) => (
          <Link
            key={i}
            to={article.to}
            className="bg-white/5 border border-white/10 rounded-xl p-5 hover:border-[#2ABFBF]/30 hover:bg-white/[0.07] transition-all group"
            data-testid={`citation-article-${i}`}
          >
            <h3 className="text-white font-semibold text-sm mb-2 group-hover:text-[#2ABFBF] transition-colors">{article.label}</h3>
            <p className="text-gray-500 text-xs">{article.desc}</p>
          </Link>
        ))}
      </div>
    </section>

    {/* Citation Leaderboard */}
    <section className="container-custom max-w-5xl mx-auto px-4 mb-20">
      <CitationLeaderboard />
    </section>

    {/* Guides & Resources */}
    <section className="container-custom max-w-5xl mx-auto px-4 mb-20">
      <div className="flex items-center gap-3 mb-8">
        <BookOpen className="w-6 h-6 text-[#2ABFBF]" />
        <h2 className="text-2xl font-bold text-white">Guides & Resources</h2>
      </div>
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {GUIDES.map((guide, i) => {
          const GIcon = guide.icon;
          return (
            <Link
              key={i}
              to={guide.to}
              className="flex items-center gap-3 bg-white/5 border border-white/10 rounded-xl p-4 hover:border-[#2ABFBF]/30 hover:bg-white/[0.07] transition-all group"
              data-testid={`guide-${i}`}
            >
              <GIcon className="w-5 h-5 text-gray-500 group-hover:text-[#2ABFBF] transition-colors shrink-0" />
              <span className="text-gray-300 text-sm group-hover:text-white transition-colors">{guide.label}</span>
            </Link>
          );
        })}
      </div>
    </section>

    {/* Local Coverage */}
    <section className="container-custom max-w-5xl mx-auto px-4 mb-20">
      <div className="flex items-center gap-3 mb-8">
        <MapPin className="w-6 h-6 text-[#2ABFBF]" />
        <h2 className="text-2xl font-bold text-white">Texas Local Pages</h2>
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
        {CITIES.map(city => (
          <div key={city.slug} className="bg-white/5 border border-white/10 rounded-xl p-4" data-testid={`learn-city-${city.slug}`}>
            <h3 className="text-white font-semibold text-sm mb-2">{city.name}</h3>
            <div className="space-y-1">
              <Link to={`/seo-${city.slug}`} className="block text-xs text-gray-400 hover:text-green-400 transition-colors">SEO</Link>
              <Link to={`/local-seo-${city.slug}`} className="block text-xs text-gray-400 hover:text-blue-400 transition-colors">Local SEO</Link>
              <Link to={`/gbp-optimization-${city.slug}`} className="block text-xs text-gray-400 hover:text-yellow-400 transition-colors">GBP</Link>
            </div>
          </div>
        ))}
      </div>
    </section>

    {/* Bottom CTA */}
    <section className="container-custom max-w-4xl mx-auto px-4 text-center">
      <div className="bg-gradient-to-br from-[#2ABFBF]/10 to-purple-500/10 border border-[#2ABFBF]/20 rounded-2xl p-10">
        <h2 className="text-2xl font-bold text-white mb-3">Not Sure Where to Start?</h2>
        <p className="text-gray-400 mb-6 max-w-xl mx-auto">Our free AI Visibility Audit checks all layers — SEO, AEO, and GEO — and gives you a prioritized roadmap.</p>
        <Link to="/free-ai-visibility-audit">
          <Button className="bg-[#2ABFBF] hover:bg-[#1E9999] text-black font-semibold px-8" data-testid="learn-bottom-cta">
            Get Your Free AI Visibility Audit <ArrowRight className="w-4 h-4 ml-2" />
          </Button>
        </Link>
      </div>
    </section>
  </div>
  );
};

export default LearnPage;
